import _ from "lodash";
import React, { useState, useMemo } from "react";
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import { useI18n } from "Hooks";
import classNames from "classnames";
import GroupedList from "../GroupedList";
import {
  getClassList,
  formatClassesStructureAndFilterNoGrades,
} from "MyCalendar/components/EventModalV2/modules/Utils";
import {
  getOrganizationCoursesQuery,
  getUserCoursesV2Query,
} from "MyCalendar/components/EventModalV2/modules/EventModalV2Query";
import {
  getUserEventCreationAccessLevel,
  getSelectedAcademicYearIdForSchool,
} from "MyCalendar/modules/Utils";

import { Checkbox, SearchInput } from "@toddle-edu/ds-web";

import {
  getUserId,
  getOrganizationId,
  getOrganizationCurriculumPrograms,
} from "modules/CommonSelectors";
import withLoader from "UIComponents/GqlWithLoader";
import ACLStore from "lib/aclStore";
import ACLStorage from "lib/aclService";
import {
  ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION,
  ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR,
  CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
} from "Constants/permissionConstants";

import classes from "./ClassSelectionList.scss";
import {
  useCourseCount,
  useVisibleCourses,
} from "MyCalendar/components/EventModalV2/hooks";
import { useCourseCountMapping } from "MyCalendar/components/EventModalV2/components/ParticipantContext";
import { getSelectedSchoolDetailsForCalendar } from "Teacher/routes/Courses/routes/Course/routes/MyCalendar/modules/Utils";

const ClassSelectionList = ({
  courses,
  selectedCourseList,
  setSelectedCourseList,
  selectedUserTypes,
  viewOnly,
  testId,
}) => {
  const componentTestId = testId ? `${testId}-classList` : "classList";
  const [searchText, setSearchText] = useState("");
  const { t } = useI18n();
  const { coursesRef, coursesSetRef, visibleCoursesIds } = useVisibleCourses(
    viewOnly
  );

  const courseCountMappingRef = useCourseCountMapping();
  const countMapping = courseCountMappingRef?.current;

  const { loading: loadingCount } = useCourseCount(
    visibleCoursesIds,
    viewOnly,
    courseCountMappingRef
  );
  const allClassLength = _.size(courses);
  const isAllSelected = _.size(selectedCourseList) === allClassLength;

  const classItemList = useMemo(
    () =>
      _.filter(courses, ({ title }) =>
        _.includes(_.lowerCase(title), _.lowerCase(searchText))
      ),
    [searchText, courses]
  );

  const courseListWithGrades = useMemo(
    () =>
      getClassList({
        filteredClasses: _.map(classItemList, item => ({
          ...item,
          staffCount: _.get(
            countMapping,
            `${item?.id}.collaborator.totalCount`,
            0
          ),
          familyMemberCount: _.get(
            countMapping,
            `${item?.id}.familyMembers.totalCount`,
            0
          ),
          studentCount: _.get(
            countMapping,
            `${item?.id}.students.totalCount`,
            0
          ),
        })),
      }),
    [classItemList, countMapping]
  );

  const selectAllCourses = () => {
    setSelectedCourseList(
      isAllSelected
        ? []
        : // using unique as class can be in multiple grade, flatmap with duplicate classes
          _.uniqBy(
            _.flatMap(courseListWithGrades, ({ options }) => options),
            "id"
          )
    );
  };

  const scrollContainerClasses = classNames({
    [classes.scrollContainer]: true,
    [classes.viewOnlyScrollContainer]: viewOnly,
  });

  return (
    <>
      {viewOnly ? null : (
        <div className={classes.searchContainer}>
          <SearchInput
            focusSearchBySlash={true}
            placeholder={t("announcement:search_classes")}
            onChange={setSearchText}
            value={searchText}
            testId={componentTestId}
          />
        </div>
      )}
      <div className={scrollContainerClasses}>
        {viewOnly || _.size(searchText) ? null : (
          <div
            className={classes.selectAllContainer}
            onClick={selectAllCourses}
            data-test-id={`${componentTestId}-selectAll-button`}
          >
            <div className={classes.avatarLabelContainer}>
              <div className="text-label">{t("common:all_classes_plural")}</div>
            </div>
            <Checkbox
              isChecked={isAllSelected}
              testId={`${componentTestId}-all`}
            />
          </div>
        )}
        <GroupedList
          viewOnly={viewOnly}
          itemList={courseListWithGrades}
          selectedItemsList={selectedCourseList}
          setSelectedItemsList={setSelectedCourseList}
          selectedUserTypes={selectedUserTypes}
          testId={componentTestId}
          coursesRef={coursesRef}
          coursesSetRef={coursesSetRef}
          loadingCount={loadingCount}
          countMapping={courseCountMappingRef.current}
          isNotMultiNodeCount={false}
        />
      </div>
    </>
  );
};

const mapStateToProps = state => {
  const userId = getUserId(state);
  const organizationId = getOrganizationId(state);

  const { currentCurriculumProgramId } = getSelectedSchoolDetailsForCalendar(
    state
  );
  const academicYearId = getSelectedAcademicYearIdForSchool(state);

  const organizationCurriculumPrograms = getOrganizationCurriculumPrograms(
    state
  );

  const programsUserCanPublishTo = _.filter(
    organizationCurriculumPrograms,
    ({ id }) =>
      ACLStorage.getPermissionValueByContext(
        CAN_MANAGE_CALENDAR_EVENTS_AND_MEETINGS_PERMISSION,
        [
          {
            entityId: id,
            entityType: "CURRICULUM_PROGRAM",
          },
        ]
      )
  );

  const userEventCreationAccessLevel = getUserEventCreationAccessLevel({
    currentCurriculumProgramId,
    alsoCheckClassLevelPermission: false,
  });

  const userCanCreateHolidaysAndWholeSchoolEvents = _.includes(
    ["ACCOUNT_LEVEL", "PROGRAM_LEVEL"],
    userEventCreationAccessLevel
  );

  const userHasAccoutLevelAccess = _.includes(
    ["ACCOUNT_LEVEL"],
    userEventCreationAccessLevel
  );

  return {
    userId,
    academicYearId,
    currentCurriculumProgramId,
    organizationId,
    userCanCreateHolidaysAndWholeSchoolEvents,
    userHasAccoutLevelAccess,
    isData: true,
    isLoading: false,
    // style for the loading container
    containerStyle: {
      height: "100%",
    },
    programsUserCanPublishTo,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getOrganizationCoursesQuery, {
    name: "getOrganizationCourses",
    skip: ({ userCanCreateHolidaysAndWholeSchoolEvents, viewOnly }) =>
      !userCanCreateHolidaysAndWholeSchoolEvents || viewOnly,
    options: ({
      organizationId,
      academicYearId,
      userHasAccoutLevelAccess,
      programsUserCanPublishTo,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        organizationId,
        courseFilters: {
          academicYears: [academicYearId],
          ...(!userHasAccoutLevelAccess
            ? {
                curriculumProgramIds: _.map(
                  programsUserCanPublishTo,
                  ({ id }) => id
                ),
              }
            : {}),
        },
        getGrades: true,
        getCurriculumProgram: false,
      },
    }),
    props({ getOrganizationCourses }) {
      const node = _.get(getOrganizationCourses, "node", {});

      return {
        isData: !_.isEmpty(node),
        courses: formatClassesStructureAndFilterNoGrades(
          _.get(node, "courses.edges")
        ),
        isLoading: _.includes([1, 2], getOrganizationCourses.networkStatus),
      };
    },
  }),
  graphql(getUserCoursesV2Query, {
    name: "getUserCoursesV2",
    skip: ({ userCanCreateHolidaysAndWholeSchoolEvents, viewOnly }) =>
      userCanCreateHolidaysAndWholeSchoolEvents || viewOnly,
    options: ({ userId, academicYearId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        filters: {
          fetchOnlyMappedCourses: false,
          academicYears: [academicYearId],
          ...(ACLStore.can(ENABLE_ROLES_AND_PERMISSIONS_CONSUMPTION)
            ? {
                controlParameters: [ASSIGNMENTS_MANAGE_PERMISSION_IN_CALENDAR],
              }
            : {}),
        },
        id: userId,
        getGrades: true,
        getCurriculumProgram: false,
        userType: "STAFF",
      },
    }),
    props({ getUserCoursesV2 }) {
      const node = _.get(getUserCoursesV2, "node", {});
      return {
        isData: !_.isEmpty(node),
        courses: formatClassesStructureAndFilterNoGrades(
          _.get(node, "coursesV2.edges")
        ),
        isLoading: _.includes([1, 2], getUserCoursesV2["networkStatus"]),
      };
    },
  }),
  withLoader
)(ClassSelectionList);
